import { useEffect, useState } from 'react'
import { Progress, Badge, Text, Icon, cn } from '@flatfile/baseline'
import { motion, AnimatePresence } from 'framer-motion'
import { useEnrichmentStatus } from '../../hooks/useEnrichmentStatus'

interface EnrichmentProgressOverlayProps {
  sheetId?: string
  className?: string
}

export function EnrichmentProgressOverlay({
  sheetId,
  className,
}: EnrichmentProgressOverlayProps) {
  const { status, hasRunningEnrichments } = useEnrichmentStatus(sheetId)
  const [isMinimized, setIsMinimized] = useState(false)
  const [shouldShow, setShouldShow] = useState(false)

  // Show overlay when there are running enrichments
  useEffect(() => {
    if (hasRunningEnrichments) {
      setShouldShow(true)
    } else {
      // Keep showing for 2 seconds after completion
      const timer = setTimeout(() => {
        setShouldShow(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [hasRunningEnrichments])

  if (!shouldShow || !status) {
    return null
  }

  const runningEnrichments = status.enrichments.filter(
    (e) => e.status === 'running'
  )

  // Calculate overall progress
  const totalProgress = runningEnrichments.reduce((acc, enr) => {
    const progress = enr.totalRecords
      ? (enr.recordsProcessed || 0) / enr.totalRecords
      : 0
    return acc + progress
  }, 0)
  const averageProgress =
    runningEnrichments.length > 0
      ? (totalProgress / runningEnrichments.length) * 100
      : 100

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={cn(
          'fixed top-20 right-4 z-50 bg-background border rounded-lg shadow-lg',
          isMinimized ? 'w-12 h-12' : 'w-80',
          className
        )}
      >
        {isMinimized ? (
          <button
            onClick={() => setIsMinimized(false)}
            className='w-full h-full flex items-center justify-center hover:bg-muted/50 rounded-lg'
          >
            <Icon name='lightning' className='h-5 w-5 text-primary' />
          </button>
        ) : (
          <div className='p-4'>
            <div className='flex items-center justify-between mb-3'>
              <div className='flex items-center gap-2'>
                <Icon name='lightning' className='h-4 w-4 text-primary' />
                <Text className='font-medium'>Enrichment Progress</Text>
              </div>
              <button
                onClick={() => setIsMinimized(true)}
                className='p-1 hover:bg-muted/50 rounded'
              >
                <Icon name='minus' className='h-4 w-4' />
              </button>
            </div>

            {hasRunningEnrichments ? (
              <>
                <div className='mb-3'>
                  <Progress value={averageProgress} className='h-2' />
                  <Text className='text-xs text-muted-foreground mt-1'>
                    {Math.round(averageProgress)}% complete
                  </Text>
                </div>

                <div className='space-y-2'>
                  {runningEnrichments.map((enr) => {
                    const progress = enr.totalRecords
                      ? ((enr.recordsProcessed || 0) / enr.totalRecords) * 100
                      : 0

                    return (
                      <div key={enr.id} className='p-2 bg-muted/30 rounded-md'>
                        <div className='flex items-center justify-between mb-1'>
                          <Text className='text-sm truncate flex-1'>
                            {enr.name}
                          </Text>
                          <Badge variant='secondary' className='text-xs'>
                            {enr.recordsProcessed || 0}/{enr.totalRecords || 0}
                          </Badge>
                        </div>
                        <Progress value={progress} className='h-1' />
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <div className='text-center py-4'>
                <Icon
                  name='check-circle'
                  className='h-8 w-8 text-green-600 mx-auto mb-2'
                />
                <Text className='text-sm font-medium'>
                  All enrichments completed!
                </Text>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}
